import { useMemo, useState } from 'react'
import { cn } from 'components/UI/cn'

interface SectionWithContentProps {
  title: string
  content: string
}

// Items shown before the "+N more" toggle appears.
const DEFAULT_VISIBLE_ITEMS = 10

// Cap height of the expanded list so a thousand-item summary stays scrollable
// within the panel instead of stretching the sidebar indefinitely.
const EXPANDED_MAX_HEIGHT_PX = 200

const ITEM_SEPARATOR = ', '

export function SectionWithContent({
  title,
  content,
}: SectionWithContentProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const items = useMemo(
    () =>
      content
        .split(ITEM_SEPARATOR)
        .map((s) => s.trim())
        .filter(Boolean),
    [content]
  )

  const hiddenCount = Math.max(0, items.length - DEFAULT_VISIBLE_ITEMS)
  const hasMore = hiddenCount > 0
  const visibleItems = isExpanded
    ? items
    : items.slice(0, DEFAULT_VISIBLE_ITEMS)

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-normal text-gray-600 capitalize">
        {title}
      </div>
      <div
        className={cn(
          'text-sm font-medium text-gray-900',
          isExpanded && 'overflow-y-auto'
        )}
        style={{
          maxHeight: isExpanded ? `${EXPANDED_MAX_HEIGHT_PX}px` : undefined,
        }}
      >
        {visibleItems.join(ITEM_SEPARATOR)}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="self-start text-sm font-medium text-brand-bright hover:text-brand-active transition-colors bg-transparent border-none p-0 cursor-pointer"
        >
          {isExpanded ? 'Show less' : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  )
}
